<script setup lang="ts">
import { ref, onMounted, onUnmounted, inject, Ref, computed } from 'vue'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Progress } from '@/components/ui/progress'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { RefreshCw, Clock, Loader2, ImageIcon, Film, Music, FileIcon } from 'lucide-vue-next'
import axios from 'axios'
import { useMedia } from '@/services/Media'
import { toast } from 'vue-sonner'

const vhostId = inject<Ref<string>>('vhostId', ref('0'))

interface QueueItem {
	mid: number
	uid: number
	status: number
	filetype: number
	vhost: number
	date: string
	percentage: number
}

const queue = ref<QueueItem[]>([])
const loading = ref(false)
const totalCount = ref(0)
const pollingTimer = ref<number | null>(null)
const typeFilter = ref('all')

const filteredQueue = computed(() => {
	if (typeFilter.value === 'all') return queue.value
	return queue.value.filter((item) => String(item.filetype) === typeFilter.value)
})

const queuedCount = computed(() => queue.value.filter((i) => i.status === 1).length)
const convertingCount = computed(() => queue.value.filter((i) => i.status !== 1).length)

const fetchQueue = async () => {
	loading.value = true
	try {
		const { data } = await axios.post('/services/json', {
			method: 'media.getConverterQueue',
			vhostID: vhostId.value,
		})
		if (data.status) {
			queue.value = Array.isArray(data.result?.data) ? data.result.data : Array.isArray(data.result) ? data.result : []
			totalCount.value = data.result?.totalCount ?? queue.value.length
		}
	} catch (e) {
		console.error('Error fetching converter queue:', e)
	} finally {
		loading.value = false
	}
}

const reconvert = async (mediaId: number) => {
	try {
		const { queueReconversion } = useMedia()
		const result = await queueReconversion(mediaId)
		if (result) {
			toast('Queued', { description: `Media ${mediaId} queued for re-conversion.` })
			await fetchQueue()
		} else {
			toast('Error', { description: 'Failed to queue re-conversion.' })
		}
	} catch (e) {
		toast('Error', { description: 'Failed to queue re-conversion.' })
	}
}

const getFileTypeLabel = (ft: number) => {
	if (ft === 1) return 'Image'
	if (ft === 2) return 'Video'
	if (ft === 3) return 'Audio'
	return 'Other'
}

const getFileTypeIcon = (ft: number) => {
	if (ft === 1) return ImageIcon
	if (ft === 2) return Film
	if (ft === 3) return Music
	return FileIcon
}

const startPolling = () => {
	stopPolling()
	pollingTimer.value = window.setInterval(fetchQueue, 10000)
}

const stopPolling = () => {
	if (pollingTimer.value !== null) {
		clearInterval(pollingTimer.value)
		pollingTimer.value = null
	}
}

onMounted(() => {
	fetchQueue()
	startPolling()
})

onUnmounted(() => {
	stopPolling()
})
</script>

<template>
	<div class="p-4 space-y-4">
		<div class="flex justify-end">
			<Button variant="outline" @click="fetchQueue">
				<RefreshCw class="h-4 w-4 mr-2" />
				Refresh
			</Button>
		</div>

		<div class="grid gap-4 md:grid-cols-3">
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Total in Queue</CardTitle>
					<Loader2 class="h-5 w-5 text-muted-foreground" />
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold">{{ totalCount }}</div>
				</CardContent>
			</Card>
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Queued</CardTitle>
					<Clock class="h-5 w-5 text-yellow-500" />
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold">{{ queuedCount }}</div>
				</CardContent>
			</Card>
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Converting</CardTitle>
					<Loader2 class="h-5 w-5 text-blue-500 animate-spin" />
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold">{{ convertingCount }}</div>
				</CardContent>
			</Card>
		</div>

		<Card>
			<CardHeader>
				<div class="flex justify-between items-center">
					<div>
						<CardTitle class="text-lg font-normal">Queue Items</CardTitle>
						<CardDescription>{{ totalCount }} items in the conversion pipeline</CardDescription>
					</div>
					<Select v-model="typeFilter">
						<SelectTrigger class="w-36">
							<SelectValue placeholder="Filter by type" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All Types</SelectItem>
							<SelectItem value="1">Images</SelectItem>
							<SelectItem value="2">Videos</SelectItem>
							<SelectItem value="3">Audio</SelectItem>
						</SelectContent>
					</Select>
				</div>
			</CardHeader>
			<CardContent>
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>Media ID</TableHead>
							<TableHead>Type</TableHead>
							<TableHead>Status</TableHead>
							<TableHead>Upload Date</TableHead>
							<TableHead>Progress</TableHead>
							<TableHead class="text-right">Action</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						<template v-if="filteredQueue.length > 0">
							<TableRow v-for="item in filteredQueue" :key="item.mid">
								<TableCell>
									<router-link :to="'/media/' + item.mid" class="text-primary hover:underline font-medium">
										{{ item.mid }}
									</router-link>
								</TableCell>
								<TableCell>
									<div class="flex items-center gap-1.5">
										<component :is="getFileTypeIcon(item.filetype)" class="h-4 w-4 text-muted-foreground" />
										{{ getFileTypeLabel(item.filetype) }}
									</div>
								</TableCell>
								<TableCell>
									<Badge :variant="item.status === 1 ? 'outline' : 'secondary'">
										<Clock v-if="item.status === 1" class="h-3 w-3 mr-1" />
										<Loader2 v-else class="h-3 w-3 mr-1 animate-spin" />
										{{ item.status === 1 ? 'Queued' : 'Converting' }}
									</Badge>
								</TableCell>
								<TableCell class="text-sm text-muted-foreground">{{ item.date }}</TableCell>
								<TableCell>
									<div class="flex items-center gap-2 min-w-[120px]">
										<Progress :model-value="item.percentage" class="h-2 flex-1" />
										<span class="text-xs text-muted-foreground w-10 text-right">{{ item.percentage }}%</span>
									</div>
								</TableCell>
								<TableCell class="text-right">
									<Button variant="outline" size="sm" @click="reconvert(item.mid)">
										<RefreshCw class="h-3 w-3 mr-1" /> Re-Convert
									</Button>
								</TableCell>
							</TableRow>
						</template>
						<TableRow v-else>
							<TableCell colspan="6" class="h-24 text-center text-muted-foreground">
								<div v-if="loading">Loading converter queue...</div>
								<div v-else>No items in the conversion queue.</div>
							</TableCell>
						</TableRow>
					</TableBody>
				</Table>
			</CardContent>
		</Card>
	</div>
</template>
